/* eslint-disable max-lines */
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Output,
  ViewChild,
  OnInit,
  OnDestroy,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { cloneDeep, capitalize, isEqual } from 'lodash-es';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';

import {
  Company,
  EventTypes,
  Project,
  User,
  List,
  ListItem,
  ListProperty,
  ListEntryTypes,
  ListAttachment,
  StyleVariants,
  ListPropertySubscription,
  PlanTypeLimits,
  DashpivotModules,
} from '@site-mate/dashpivot-shared-library';
import { CompanyPlanTypes } from '@site-mate/sitemate-global-shared/lib';

import { TmpI18NService } from 'app/i18n/tmp-i18n.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ListsHandler } from 'app/shared/model/lists/lists-handler.model';
import { OrthogonalDirection } from 'app/shared/model/orthogonal-direction.enum';
import { IdNormalizer } from 'app/shared/model/utils/id-normalizer.model';
import { ConfirmService } from 'app/shared/service/confirm.service';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { EventsService } from 'app/shared/service/events/events.service';
import { AccountUpgradeLimitComponent } from 'app/user/account-upgrade/account-upgrade-limit/account-upgrade-limit.component';
import { UserService } from 'app/user/user.service';

import { ListItemPropertyMap } from './list-item-property-map.type';
import { ListPropertyColumnMovedEvent } from '../list-header-edit/list-property-column-moved.interface';
import { ListItemSelectionService } from '../list-item-selection.service';
import { ListPropertyAddComponent } from '../list-property-add/list-property-add.component';
import { ListPropertyService } from '../list-property.service';
import { ListsService } from '../lists.service';

interface ListItemPropertyUpdate {
  value: string | number | ListAttachment[];
}

type ListPropertyUpdate = {
  id: string;
  index?: number;
  name?: string;
  subscribedUsers?: ListPropertySubscription;
};

@Component({
  selector: 'cc-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class ListComponent implements OnInit, OnDestroy, OnChanges {
  @Input() folder: {
    hierarchy: Exclude<AppHierarchy, AppHierarchy.Team>;
    project?: Project;
    company?: Company;
    planType?: CompanyPlanTypes;
  };
  @Input() users: User[];
  @Input() canEditData: boolean;
  @Input() isUploading = false;
  @Input() isUpdating = false;
  @Input() canAddNewItem = false;
  @Output() readonly listDeployed = new EventEmitter<List>();
  @Output() readonly listUndeployed = new EventEmitter<ListItem>();
  @Output() readonly listChanged = new EventEmitter<{
    list: List;
    insertNew: boolean;
    skipPersistence?: boolean;
  }>();

  @Output() readonly listUploaded = new EventEmitter<{ fileList: FileList; list: List }>();
  @Output() readonly listDeployedViaCsv = new EventEmitter<{ fileList: FileList; list: List }>();
  @Output() readonly uploadFileEvent = new EventEmitter<boolean>();
  @Output() readonly openWarningModalEvent = new EventEmitter<boolean>();
  @Output() readonly addListItemStarted = new EventEmitter<void>();
  @Output() readonly listItemAdded = new EventEmitter<{ position: number; value: string; list: List }>();
  @Output() readonly listItemUpdated = new EventEmitter<{
    list: List;
    itemId: string;
    value?: string;
    style?: StyleVariants;
  }>();
  @Input() triggerUploadListCSV: EventEmitter<void>;

  listItemPropertyMap: ListItemPropertyMap = new Map();
  addPropertyIndex: number | null = null;
  beforeAddPropertyIterator: Array<void> = [];
  afterAddPropertyIterator: Array<void> = [];
  selectedItems = this.listItemSelectionService.selectedItems$;
  expandedRow: string | null = null;
  listDefaultStyle: StyleVariants;
  isDeployedList: boolean;
  private readonly allItemsIds = new Set<string>();
  private _list: List;
  private unSubscribe = new Subject<void>();

  readonly styleVariants = StyleVariants;

  @Input() set list(list: List) {
    this.rebuildListItemPropertyMap(list);
    this._list = list;
    if (list.properties) {
      this.rebuildAddPropertyIterators(this.addPropertyIndex);
    }

    this.setAllItemsIds(list);
  }
  get list(): List {
    return this._list;
  }

  private setAllItemsIds(list: List) {
    this.allItemsIds.clear();

    if (!list.items) {
      return;
    }

    list.items.forEach((item) => {
      this.allItemsIds.add(item._id);
    });
  }

  private rebuildListItemPropertyMap(list: List) {
    list.items?.forEach((item) => {
      const itemPropertyMap = new Map(item.properties?.map((property) => [property._id, property]) || []);
      this.listItemPropertyMap.set(item._id, itemPropertyMap);
    });
  }

  isScrolling = false;
  isInCreatePropertyMode = false;
  readonly listEntryTypes = ListEntryTypes;
  readonly listsHandler = ListsHandler;
  downloadTooltipContent = `Download CSV`;
  tooltipHoverStates = { upload: false, download: false };
  listPropertiesLimit: number;
  isUpgradeModalVisible = false;
  canEditProperties = false;

  @ViewChild('addListProperty') addPropertyComponent: ListPropertyAddComponent;
  @ViewChild('fileUploadInput') fileUploadInput: ElementRef;
  @ViewChild('fileUploadDeployedInput') fileUploadDeployedInput: ElementRef;

  constructor(
    private readonly confirmService: ConfirmService,
    private readonly eventsService: EventsService,
    private readonly userService: UserService,
    private readonly listItemSelectionService: ListItemSelectionService,
    private readonly listPropertyService: ListPropertyService,
    private readonly listsService: ListsService,
    private readonly zone: NgZone,
    private readonly errorHandler: ErrorHandler,
    private readonly ngbModal: NgbModal,
    protected readonly i18nService: TmpI18NService,
  ) {}

  ngOnInit() {
    this.listDefaultStyle = this._list.defaultStyle;
    this.isUpgradeModalVisible = this.shouldDisplayUpgradeModal();
    this.setListLimit();
    this.isDeployedList = this.list?.deployedParents?.includes(this.parentId);

    this.canEditProperties =
      this.folder?.hierarchy === AppHierarchy.Project
        ? this.userService.isProjectControllerOrAbove()
        : this.userService.isCompanyControllerOrAbove();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && (changes.folder || changes.list)) {
      this.isUpgradeModalVisible = this.shouldDisplayUpgradeModal();
      this.setListLimit();
    }
  }

  readonly uniqueById = (_index: number, item: { id: string; _id: string }) => new IdNormalizer(item).value();
  readonly uniqueByFieldType = (_index: number, entry: { id: string; kind: ListEntryTypes; value: any }) => {
    return entry.kind === ListEntryTypes.Attachment ? entry.id : entry;
  };

  get currentUser() {
    return this.userService.getCurrentUser();
  }

  get parentId() {
    return this.folder?.company?.id || this.folder?.project?.id;
  }

  get isCreatingNewListItem() {
    return this.list.items?.some((listItem) => listItem.isNew);
  }

  get hasReachedPlanLimit() {
    return this.list.properties?.length >= this.listPropertiesLimit;
  }

  setListLimit() {
    this.listPropertiesLimit =
      PlanTypeLimits[DashpivotModules.Lists].properties[this.folder?.planType] ??
      PlanTypeLimits[DashpivotModules.Lists].properties[CompanyPlanTypes.Legacy];
  }

  shouldDisplayUpgradeModal(): boolean {
    const canUpgradePlanType = [
      CompanyPlanTypes.Standard,
      CompanyPlanTypes.Legacy,
      CompanyPlanTypes.Pro,
    ].includes(this.folder?.planType);
    return canUpgradePlanType;
  }

  async onStartAddProperty(index?: number) {
    if (this.hasReachedPlanLimit) {
      this.openUpgradeModal();
      return;
    }

    if (index !== undefined && index !== this.list.properties.length) {
      this.rebuildAddPropertyIterators(index);
      await firstValueFrom(this.zone.onStable);
    }
    this.isInCreatePropertyMode = true;
  }

  openUpgradeModal() {
    const upgradeModal = this.ngbModal.open(AccountUpgradeLimitComponent, {
      windowClass: 'modal-500',
      modalDialogClass: 'modal-dialog-top',
    });

    upgradeModal.componentInstance.title = `Unlock more properties!`;
    upgradeModal.componentInstance.subtitle = this.i18nService.getMessage('upgradePlanToUnlockLimit');
    upgradeModal.componentInstance.imageSrc = `/assets/images/usage-limits/properties.svg`;
    upgradeModal.componentInstance.imageAlt = 'text and photo fields illustration';
  }

  cancelAddPropertyInBetween() {
    this.rebuildAddPropertyIterators(null);
    this.isInCreatePropertyMode = false;
  }

  onListPropertyAddedInBetween(list: List) {
    this.onListPropertyUpdated(list);
    this.rebuildAddPropertyIterators(null);
  }

  onListPropertyUpdated(list: List) {
    this.listChanged.emit({ list, insertNew: false, skipPersistence: true });
    this.isInCreatePropertyMode = false;
  }

  private rebuildAddPropertyIterators(newAddPropertyIndex: number | null) {
    this.addPropertyIndex = newAddPropertyIndex;
    const addPropertyIndexIncrement = newAddPropertyIndex || 0;
    this.beforeAddPropertyIterator = new Array(addPropertyIndexIncrement);
    this.afterAddPropertyIterator = new Array(this.list.properties.length - addPropertyIndexIncrement);
  }

  setItemEditMode(item: ListItem, isInEditMode: boolean) {
    item.editing = isInEditMode;
    if (isInEditMode === true) {
      this.expandRow(item._id);
    }
  }

  onListDeployed(list: List) {
    this.listDeployed.emit(list);
  }

  openFileUpload() {
    this.fileUploadInput.nativeElement.click();
  }

  onListDownloaded() {
    const newLine = '\n';
    const formattedItems = this.list.items.map((item) => String(item.value)).join(newLine);
    saveAs(new Blob([formattedItems]), `${this.list.name}.csv`);
  }

  onListUploaded(fileList: FileList) {
    this.listUploaded.emit({ list: this.list, fileList });
    this.eventsService.trackEvent(EventTypes.ListItemActionApplied, { Context: 'Uploaded From CSV' });
  }

  onListUploadedWithTargetReset(fileList: FileList) {
    this.onListUploaded(fileList);
  }

  onListItemEditCancelled(item: ListItem) {
    item.editing = false;
    if (item.isNew) {
      this.list.items = this.list.items.filter((entry) => !entry.isNew);
      this.setAllItemsIds(this.list);
    }
    this.setItemEditMode(item, false);
  }

  onListItemEdited(item: ListItem, value: string) {
    if (!value) {
      this.onListItemEditCancelled(item);
      return;
    }

    item.value = value;
    delete item.editing;

    if (item.isNew) {
      this.handleListItemAdded(item);
      return;
    }

    const index = this.list.items.findIndex(({ _id }) => _id === item._id);
    this.list.items[index] = item;

    this.listItemUpdated.emit({
      itemId: item._id,
      list: this.list,
      value,
    });
  }

  private handleListItemAdded(item: ListItem) {
    if (!item.isNew) {
      return;
    }

    const index = this.list.items.length - 1;
    this.listItemAdded.emit({ position: index, value: item.value, list: this.list });
  }

  onListHeaderPropertyChanged(property: ListProperty) {
    const propertyIndex = this.list.properties.findIndex((p) => p.id === property.id);
    const oldProperty = cloneDeep(this.list.properties[propertyIndex]);

    this.onListPropertyEdited(property as any).subscribe({
      next: () => this.list.properties.splice(propertyIndex, 1, property),
      error: (error) => {
        this.list.properties.splice(propertyIndex, 1, oldProperty);
        this.errorHandler.handle(error);
      },
    });
  }

  private onListPropertyEdited(params: ListPropertyUpdate) {
    const { id, name, subscribedUsers } = params;
    const index = params.index ?? this.list.properties.findIndex((entry) => entry.id === id);
    const updatedProperty = { name, index } as Partial<ListProperty>;

    if (subscribedUsers && !isEqual(subscribedUsers, this.list.properties[index].subscribedUsers)) {
      updatedProperty.subscribedUsers = subscribedUsers;
    }

    return this.listPropertyService.update(
      this.list._id,
      id,
      updatedProperty,
      this.parentId,
      this.folder.hierarchy,
    );
  }

  onListPropertyDeleted(property: ListProperty) {
    this.confirmService
      .confirmDelete('deleteListProperty')
      .then(() => {
        this.listPropertyService
          .delete(this.list.id, property._id, this.parentId, this.folder.hierarchy)
          .subscribe((list) => {
            this.onListPropertyUpdated(list);
          });
      })
      .catch(() => {
        // no op
      });
  }

  onListItemStyleUpdated(item, newStyle: StyleVariants) {
    const localList = cloneDeep(this.list);
    const itemIndex = localList.items.findIndex((localItem) => localItem.id === item.id);

    if (itemIndex !== -1) {
      localList.items[itemIndex].style = newStyle;
      this.listItemUpdated.emit({ itemId: item._id, list: this.list, style: newStyle });
    }

    this.eventsService.trackEvent(EventTypes.ListItemColourEdited, { Context: capitalize(newStyle) });
  }

  onListDefaultStyleUpdated(newStyle: StyleVariants) {
    this.listsService
      .updateList(this.folder, this.list._id, { defaultStyle: newStyle })
      .pipe(takeUntil(this.unSubscribe))
      .subscribe({
        next: () => {
          this.list.defaultStyle = newStyle;
        },
        error: (error) => {
          this.listDefaultStyle = this.list.defaultStyle;
          this.errorHandler.handle(error);
        },
      });
  }

  onListCellUpdated(item: ListItem, { value, index }: { value: ListItemPropertyUpdate; index: number }) {
    const itemIndex = this.list.items.findIndex((localRow) => localRow.id === item.id);
    const property = this.list.items[itemIndex].properties[index];

    if (itemIndex === -1 || !property) {
      return;
    }

    this.listPropertyService
      .updateCell({
        listId: this.list._id,
        rowId: item._id,
        propertyId: property._id,
        value,
        parentId: this.parentId,
        hierarchy: this.folder.hierarchy,
      })
      .subscribe({ error: (error) => this.errorHandler.handle(error) });
  }

  onPropertyMoved(event: ListPropertyColumnMovedEvent) {
    if (event.direction === OrthogonalDirection.Left) {
      this.movePropertyLeft(event.index);
    } else if (event.direction === OrthogonalDirection.Right) {
      this.movePropertyRight(event.index);
    }
    this.notifyMovePropertyEvent(event.direction);
  }

  private movePropertyLeft(oldIndex: number) {
    const index = oldIndex - 1;
    const { name, id } = this.moveProperty(oldIndex, index);

    this.onListPropertyEdited({ id, index, name }).subscribe({
      error: (error) => {
        this.errorHandler.handle(error);
        this.moveProperty(index, oldIndex);
      },
    });
  }

  private movePropertyRight(oldIndex: number) {
    const index = oldIndex + 1;
    const { name, id } = this.moveProperty(oldIndex, index);

    this.onListPropertyEdited({ id, index, name }).subscribe({
      error: (error) => {
        this.errorHandler.handle(error);
        this.moveProperty(index, oldIndex);
      },
    });
  }

  private moveProperty(oldIndex: number, newIndex: number) {
    const [property] = this.list.properties.splice(oldIndex, 1);
    this.list.properties.splice(newIndex, 0, property);

    this.list.items.forEach((item) => {
      const [itemProperty] = item.properties.splice(oldIndex, 1);
      item.properties.splice(newIndex, 0, itemProperty);
    });

    return property;
  }

  private notifyMovePropertyEvent(direction: ListPropertyColumnMovedEvent['direction']) {
    this.eventsService.trackEvent(EventTypes.ListPropertyColumnMoveClicked, {
      Context: direction,
    });
  }

  onTooltipTriggerDownload(isShown: boolean): void {
    this.tooltipHoverStates.download = isShown;
  }

  onTooltipTriggerUpload(isShown: boolean): void {
    this.tooltipHoverStates.upload = isShown;
  }

  startAddItem() {
    this.addListItemStarted.emit();
  }

  get shouldDisplayEmptyState() {
    if (!this.list) {
      return false;
    }

    if (this.isDeployedList && !this.hasItems) {
      return true;
    }

    const hasItemsButCantEditProperty = this.hasItems && !this.hasProperties && !this.canEditProperties;
    if (!this.isDeployedList && hasItemsButCantEditProperty) {
      return false;
    }

    if (!this.isDeployedList && this.hasNoItemsOrProperties) {
      return true;
    }

    return false;
  }

  isSelected(itemId) {
    return this.listItemSelectionService.selectedItems.has(itemId);
  }

  get hasItems() {
    return this.list.items?.length && !this.list.items[0].isNew;
  }

  get hasProperties() {
    return this.list.properties?.length;
  }

  private get hasNoItemsOrProperties() {
    return !this.hasItems || !this.hasProperties;
  }

  toggleItemSelection(itemId: string) {
    this.listItemSelectionService.toggleItemSelection(itemId);
  }

  toggleSelectAllItems() {
    this.listItemSelectionService.toggleAllItemsSelection(this.allItemsIds);
  }

  toggleExpandItem(itemId: string) {
    if (this.expandedRow === itemId) {
      this.collapseRow();
    } else {
      this.expandRow(itemId);
    }
  }

  shouldExpandRow(itemId: string, propertyHeader: ListProperty) {
    const expandablePropertyTypes = [ListEntryTypes.Text, ListEntryTypes.Number, ListEntryTypes.Attachment];
    if (expandablePropertyTypes.includes(propertyHeader.kind)) {
      this.expandRow(itemId);
    }
  }

  expandRow(itemId: string) {
    this.expandedRow = itemId;
  }

  collapseRow() {
    this.expandedRow = null;
  }

  ngOnDestroy() {
    this.listItemSelectionService.clearSelection();
    this.unSubscribe.next();
    this.unSubscribe.complete();
  }
}
